import { noop, get } from 'lodash-es';
import React, { PureComponent } from 'react';

import * as Components from './components';
import { typeConsts, widthConsts } from './constants';

import { escapeRegExp } from '../utils';

export const fjSelectWidths = widthConsts;
export const fjSelectTypes = typeConsts;

interface SelectOption {
  value: string;
  label: string;
}

type OptionInput = string | SelectOption;

interface FjSelectProps {
  options?: OptionInput[];
  onChange?: (value?: string) => void;
  keyExtractor?: (value: string, index?: number) => React.Key;
  disabled?: boolean;
  bgColor?: string;
  primaryColor?: string;
  disabledOptionColor?: string;
  dropdownBgColor?: string;
  disabledColor?: string;
  disabledBgColor?: string;
  outlineOnFocus?: boolean;
  bgColorIcon?: string;
  alignRight?: boolean;
  value?: string;
  defaultValue?: string;
  type?: string;
  className?: string;
  noBorder?: boolean;
  selectedValueColor?: string;
  selectedOptionColor?: string;
  selectedBgColor?: string;
  width?: string | number;
  textColor?: string;
  autoComplete?: string;
}

interface FjSelectState {
  currentValue: string | false | undefined;
  highlightedValue: string | null;
  open: boolean;
  innerHeight: number;
  closing: boolean;
  filterText: string | undefined;
}

class FjSelect extends PureComponent<FjSelectProps, FjSelectState> {
  static defaultProps: Partial<FjSelectProps> = {
    options: [],
    onChange: noop,
    keyExtractor: (value: string) => value,
    disabled: false,
    bgColor: '#FFFFFF',
    primaryColor: '#156777',
    disabledOptionColor: '#8EAEB2',
    dropdownBgColor: '#FFFFFF',
    disabledColor: '#B5BEBB',
    disabledBgColor: '#E9EBEA',
    outlineOnFocus: false,
    bgColorIcon: '#156777',
    alignRight: false,
  };

  optionsInner: React.RefObject<HTMLUListElement | null>;

  container: React.RefObject<HTMLDivElement | null>;

  dropdown: React.RefObject<HTMLDivElement | null>;

  opinionRefs: React.RefObject<HTMLLIElement | null>[];

  filterInputRef: HTMLInputElement | null = null;

  constructor(props: FjSelectProps) {
    super(props);
    // Initialize refs
    this.optionsInner = React.createRef();
    this.container = React.createRef();
    this.dropdown = React.createRef();
    this.opinionRefs = Array(this.options.length)
      .fill(undefined)
      .map(() => React.createRef<HTMLLIElement>());

    const firstValue = !!this.options.length && this.options[0].value;
    const initialValue = this.props.value || this.props.defaultValue || firstValue;
    const initialOption = this.getOptionByValue(initialValue);
    this.state = {
      currentValue: initialValue,
      highlightedValue: null,
      open: false,
      innerHeight: 0,
      closing: false,
      filterText: initialOption.label,
    };
  }

  static getDerivedStateFromProps(
    props: FjSelectProps,
    state: FjSelectState,
  ): Partial<FjSelectState> | null {
    if (props.value !== (state as { value?: string }).value && typeof props.value !== 'undefined') {
      return { currentValue: props.value };
    }
    return null;
  }

  // Lifecycle Methods
  componentDidMount(): void {
    this.updateInnerHeight();
  }

  componentDidUpdate(): void {
    this.updateInnerHeight();
  }

  componentWillUnmount(): void {
    document.removeEventListener('click', this.handleClick, false);
    document.removeEventListener('keydown', this.handleKeydown, false);
  }

  // Getters
  get options(): SelectOption[] {
    const { options = [] } = this.props;
    return options.map((option) => {
      if (typeof option !== 'object') {
        return {
          value: option,
          label: option,
        };
      }
      return option;
    });
  }

  getOptionByValue = (value: string | false | null | undefined): Partial<SelectOption> =>
    this.options.find((option) => option.value === value) || {};

  // Filter only applies to FORM type selects
  get filteredOptions(): SelectOption[] {
    if (this.isFormSelect) {
      const cleanText = escapeRegExp(this.state.filterText as string);
      const filter = new RegExp(`^${cleanText}|\\s+${cleanText}`, 'i');
      return this.options.filter((option) => filter.test(option.label));
    }
    return this.options;
  }

  get currentOption(): Partial<SelectOption> {
    return this.getOptionByValue(this.state.currentValue);
  }

  get highlightedOption(): Partial<SelectOption> {
    return this.getOptionByValue(this.state.highlightedValue);
  }

  getOptionIndexByValue(value: string | false | null | undefined): number | undefined {
    const index = this.filteredOptions.findIndex((option) => option.value === value);
    return index === -1 ? undefined : index;
  }

  get currentOptionIndex(): number | undefined {
    return this.getOptionIndexByValue(this.state.currentValue);
  }

  get highlightedOptionIndex(): number | undefined {
    return this.getOptionIndexByValue(this.state.highlightedValue);
  }

  get isFormSelect(): boolean {
    return this.props.type === typeConsts.FORM;
  }

  // Set height of Components.Dropdown so it is never taller than content
  updateInnerHeight = (): void =>
    this.setState({ innerHeight: get(this.optionsInner, 'current.clientHeight', 0) });

  // Open/Close methods
  open = (): void => {
    document.addEventListener('click', this.handleClick, false);
    document.addEventListener('keydown', this.handleKeydown, false);
    this.setState({ open: true });
    if (this.isFormSelect && this.filterInputRef) {
      this.setState({ filterText: '' }, () => {
        const currentIndex = this.currentOptionIndex;
        this.setHighlightedOption(currentIndex);
      });
      this.filterInputRef.focus();
    }
  };

  close = (): void => {
    document.removeEventListener('click', this.handleClick, false);
    document.removeEventListener('keydown', this.handleKeydown, false);
    this.setState({ open: false, closing: true }, () => {
      if (this.isFormSelect) {
        this.setState({
          filterText: this.currentOption.label,
        });
      }
    });
  };

  toggleOpen = (): void => (this.state.open ? this.close() : this.open());

  // Input Methods
  handleKeydown = (event: KeyboardEvent): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveHighlightedOption(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveHighlightedOption(-1);
        break;
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
      case 'Tab':
        this.selectOption();
        break;
      case 'Escape':
        this.handleCancel();
        break;
      default:
        break;
    }
  };

  handleClick = (e: MouseEvent): void => {
    const containerNode = get(this.container, 'current', { contains: noop }) as {
      contains: (node: Node | null) => boolean;
    };
    if (!containerNode.contains(e.target as Node | null)) {
      this.close();
    }
  };

  handleFilterKeydown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!this.state.open) {
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowUp':
        case 'Escape':
          this.open();
          break;
        default:
          break;
      }
    }
  };

  handleFilterTextChange = (newValue: string): void => {
    this.open();
    this.setState(
      {
        filterText: newValue,
      },
      () => {
        this.setHighlightedOption(0);
      },
    );
  };

  handleCancel = (): void => {
    if (!this.isFormSelect || this.state.filterText === '') {
      this.close();
    }
    if (this.state.open) {
      this.setState({ filterText: '' });
    }
  };

  // Option Methods
  focusOption = ({ value: highlightedValue }: SelectOption): void =>
    this.setState({ highlightedValue });

  clearHighlighted = (): void => this.setState({ highlightedValue: null });

  selectOption = ({ value: currentValue }: Partial<SelectOption> = this.highlightedOption): void =>
    this.setState({ currentValue }, () => {
      this.props.onChange!(currentValue);
      this.close();
      if (this.isFormSelect && this.filterInputRef) {
        this.filterInputRef.focus();
      }
    });

  // Compute Option States
  isSelected = (option: SelectOption): boolean => option.value === this.currentOption.value;

  isHighlighted = (option: SelectOption): boolean => option.value === this.highlightedOption.value;

  onTransitionEnd = (): void => {
    this.setState({ closing: false });
  };

  // Sets new highlighted option by index
  setHighlightedOption = (index: number | undefined): void => {
    const option = index === undefined ? undefined : this.filteredOptions[index];
    if (option) {
      this.scrollToHighlightedOption(index as number);
      this.setState({ highlightedValue: option.value });
    }
  };

  // Scroll to new option if necessary
  scrollToHighlightedOption = (index: number): void => {
    const optionRef = get(this.opinionRefs, `[${index}].current`, {}) as HTMLLIElement;
    const optionTop = optionRef.offsetTop;
    const optionBottom = optionRef.offsetTop + optionRef.clientHeight;
    const containerRef = get(this.dropdown, 'current', {}) as HTMLDivElement;
    const containerHeight = containerRef.clientHeight;
    const containerBottom = containerRef.scrollTop + containerRef.clientHeight;

    if (optionTop < containerRef.scrollTop) {
      containerRef.scrollTo(0, optionTop);
    } else if (optionBottom > containerBottom) {
      containerRef.scrollTo(0, optionBottom - containerHeight);
    }
  };

  // Highlight new option relative to currently highlighted option
  moveHighlightedOption(delta: number): void {
    const { filteredOptions } = this;
    const index = this.highlightedOptionIndex;

    let newIndex: number;
    if (index !== undefined && index >= 0) {
      newIndex = (index + delta + filteredOptions.length) % filteredOptions.length;
    } else {
      newIndex = delta > 0 ? 0 : filteredOptions.length - 1;
    }
    this.setHighlightedOption(newIndex);
  }

  renderSelectLabel(styleProps: { primaryColor?: string }): React.ReactElement {
    if (this.isFormSelect) {
      return (
        <Components.FormSelectInput
          value={this.state.filterText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            this.handleFilterTextChange(e.target.value);
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => this.handleFilterKeydown(e)}
          onFocus={this.toggleOpen}
          ref={(ref: HTMLInputElement | null) => {
            this.filterInputRef = ref;
          }}
          autoComplete={this.props.autoComplete}
          {...styleProps}
        />
      );
    }
    return <Components.Label tabIndex={0}>{this.currentOption.label}</Components.Label>;
  }

  render(): React.ReactElement {
    const { open, closing, innerHeight } = this.state;
    const {
      disabled,
      keyExtractor,
      className,
      noBorder,
      primaryColor: primaryColorProp,
      bgColor: bgColorProp,
      disabledOptionColor,
      dropdownBgColor,
      selectedValueColor: selectedValueColorProp,
      selectedOptionColor,
      selectedBgColor,
      width,
      type,
      disabledColor: disabledColorProp,
      disabledBgColor: disabledBgColorProp,
      textColor,
      outlineOnFocus,
      bgColorIcon,
      alignRight,
    } = this.props;

    const primaryColor = disabled ? disabledColorProp : primaryColorProp;
    const bgColor = disabled ? disabledBgColorProp : bgColorProp;
    const selectedValueColor = disabled ? disabledColorProp : selectedValueColorProp;

    const containerProps = { className, noBorder, width, primaryColor, disabled };
    const borderProps = {
      primaryColor,
      bgColor,
      outlineOnFocus,
      open,
      closing,
      noBorder,
      type,
      alignRight,
    };
    const dropdownContainerProps = { open, closing, innerHeight, noBorder, type };
    const fascadeProps = { selectedValueColor, textColor };
    const iconProps = { noBorder, type, bgColorIcon };
    const dropdownProps = { open, closing, innerHeight, noBorder, dropdownBgColor, type };
    const optionProps = {
      disabledOptionColor,
      dropdownBgColor,
      type,
      primaryColor,
      selectedOptionColor,
      selectedBgColor,
      textColor,
    };

    return (
      <Components.Container ref={this.container} {...containerProps}>
        <Components.Border onClick={this.toggleOpen} {...borderProps}>
          <Components.Fascade {...fascadeProps}>
            {this.renderSelectLabel({ primaryColor })}
            <Components.Icon {...iconProps} />
          </Components.Fascade>
        </Components.Border>
        <Components.DropdownContainer {...dropdownContainerProps}>
          <Components.Dropdown
            onMouseLeave={this.clearHighlighted}
            onTransitionEnd={this.onTransitionEnd}
            ref={this.dropdown}
            {...dropdownProps}
          >
            <Components.OptionList ref={this.optionsInner} alignRight={alignRight}>
              {this.filteredOptions.map((option, i) => (
                <Components.Option
                  ref={this.opinionRefs[i]}
                  selected={this.isSelected(option)}
                  highlighted={this.isHighlighted(option)}
                  onMouseOver={() => this.focusOption(option)}
                  onFocus={() => this.focusOption(option)}
                  onClick={() => this.selectOption(option)}
                  key={keyExtractor!(option.value, i)}
                  data-value={option.value}
                  {...optionProps}
                >
                  {option.label}
                </Components.Option>
              ))}
            </Components.OptionList>
          </Components.Dropdown>
        </Components.DropdownContainer>
      </Components.Container>
    );
  }
}

export default FjSelect;
